<template>
  <div
    class="acl"
    :class="`acl--${variant}`"
  >
    <aside class="acl__left hidden lg:flex">
      <div class="acl__bg">
        <div class="acl__orb acl__orb--1" />
        <div class="acl__orb acl__orb--2" />
        <div class="acl__dots" />
      </div>

      <div class="acl__brand">
        <img
          src="@/assets/images/shopimind-logo-white.svg"
          alt="ShopiMind"
          class="acl__logo"
        >
      </div>

      <div class="acl__left-main">
        <slot name="left" />
      </div>

      <div class="acl__left-footer">
        <slot name="left-footer">
          <p class="acl__copy">
            &copy; {{ year }} ShopiMind
          </p>
        </slot>
      </div>
    </aside>

    <main class="acl__right">
      <header
        v-if="$slots['right-header']"
        class="acl__right-header"
      >
        <slot name="right-header" />
      </header>

      <section
        class="acl__right-body"
        :class="{ 'acl__right-body--centered': !$slots['right-header'] }"
      >
        <slot />
      </section>

      <footer
        v-if="$slots['right-footer']"
        class="acl__right-footer"
      >
        <slot name="right-footer" />
      </footer>
    </main>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'AuthCompleteLayout',

  props: {
    variant: {
      type: String,
      default: 'complete',
      validator: (v: string) => ['complete', 'auth'].includes(v),
    },
  },

  setup() {
    const year = computed(() => new Date().getFullYear());

    return { year };
  },
});
</script>

<style lang="scss" scoped>
.acl {
  display: flex;
  width: 100%;
  min-height: 100vh;
  max-height: 100vh;
  background: #ffffff;
  overflow: hidden;

  &--auth &__left {
    flex: 0 0 45%;
    min-width: 0;
    max-width: none;
  }

  &__left {
    flex: 0 0 46%;
    min-width: 420px;
    max-width: 640px;
    position: relative;
    background: #262a27;
    color: #f8fafc;
    overflow: hidden;
    z-index: 0;
    padding: 2rem 2.5rem 1.5rem;
    border-top-right-radius: 1.5rem;
    border-bottom-right-radius: 1.5rem;
    flex-direction: column;
  }

  &__right {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    max-height: 100vh;
    overflow: hidden;
    position: relative;
    z-index: 0;
  }

  &__bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  &__orb {
    position: absolute;
    border-radius: 50%;

    &--1 {
      width: 480px;
      height: 480px;
      top: -20%;
      right: -18%;
      background: radial-gradient(circle, rgba(148, 200, 64, 0.22) 0%, rgba(148, 200, 64, 0.05) 40%, transparent 65%);
      filter: blur(40px);
    }

    &--2 {
      width: 360px;
      height: 360px;
      bottom: -15%;
      left: -15%;
      background: radial-gradient(circle, rgba(148, 200, 64, 0.12) 0%, transparent 60%);
      filter: blur(50px);
    }
  }

  &__dots {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 24px 24px;
  }

  &__brand {
    position: relative;
    z-index: 3;
    margin-bottom: 1.25rem;
  }

  &__logo {
    height: 34px;
    width: auto;
  }

  &__left-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1.75rem;
    position: relative;
    z-index: 2;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 0.25rem 1rem 0;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 2px;
    }
  }

  &__left-footer {
    position: relative;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__copy {
    margin: 0;
    font-size: 0.6875rem;
    color: rgba(255, 255, 255, 0.25);
  }

  &__right-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 2.25rem 0;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }

  &__right-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    padding: 1.5rem 2.25rem;
    min-height: 0;
    overflow-y: auto;
    position: relative;
    z-index: 1;

    &--centered {
      align-items: center;
      justify-content: center;
    }
  }

  &__right-footer {
    flex-shrink: 0;
    padding: 1rem 2.25rem;
    border-top: 1px solid rgba(148, 200, 64, 0.2);
    position: relative;
    z-index: 1;

    &:empty { display: none; }
  }
}

@media (max-width: 992px) {
  .acl {
    flex-direction: column;
    max-height: none;
    overflow: auto;

    &__left {
      display: none;
    }

    &__right {
      padding: 0;
      max-height: none;
    }
  }
}
</style>
